import React, {useState, useEffect} from "react"
import QRCode from "qrcode.react"
import Button from "@irati/ui/src/Button"
import Loading from "@irati/ui/src/Loading"
import Form from "@irati/ui/src/Form"
import {ValidationButton} from "@irati/ui/src/FormItem"
import {Label} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {mfaTracking} from "@irati-tracking/platform"
import mfaTotpService from "@irati/auth/services/mfa_totp"
import {hangPromise} from "@irati/utils/promise"
import {MFA_TYPE} from "@irati-tracking/platform/mfa"
import {MFACode} from "@irati-web/fields/mfa"
import Flex from "@irati/ui/src/Flex"
import {Hr} from "@irati/ui/src/Layout"
import Link from "@irati/ui/src/Link"
import Spacing from "@irati/ui/src/Spacing"
import {Modal} from "../../BaseModal"
import {ButtonContainer} from "../styled"
import {SelectScreen} from "../index"

interface Props extends InjectIntl {
  selectScreen: SelectScreen
  onCancel(): void
  show: boolean
}

const MFAPopupSetup: React.FC<Props> = ({show, selectScreen, formatMessage, onCancel}) => {
  const [load, setLoad] = useState<Promise<any>>(hangPromise())
  const [qr, setQr] = useState("")
  const [code, setCode] = useState("")

  useEffect(() => {
    if (show) {
      mfaTracking.popupSetupShown({type: MFA_TYPE.TOTP})
      setLoad(
        mfaTotpService.setup().then((mfaSetup) => {
          setQr(mfaSetup.code)
          mfaTracking.popupSetupComplete({type: MFA_TYPE.TOTP})
        })
      )
    } else {
      mfaTracking.popupSetupCancel({type: MFA_TYPE.TOTP})
    }
  }, [show])

  const handleSuccess = () => {
    setLoad(
      mfaTotpService.validate(code).then(() => {
        selectScreen(null)
        mfaTracking.popupSetupComplete({type: MFA_TYPE.TOTP})
      })
    )
  }

  const handleChange = (c: string) => {
    setCode(c)
  }

  return (
    <Modal show={show} header="platform.mfa.totp.setup.title">
      <Loading load={load}>
        <Flex justifyContent="center">
          <QRCode value={qr} size={200} />
        </Flex>

        <Label display="block" paddingBottom={2}>
          <Spacing marginBottom={2} />
          <FormattedMessage id="platform.mfa.totp.setup.body" />
          <Spacing marginBottom={2} />
          <FormattedMessage
            id="platform.mfa.totp.setup.hint"
            values={{
              ios: <Link href="https://apps.apple.com/us/app/google-authenticator/id388497605">ios</Link>,
              android: <Link href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">android</Link>
            }}
          />
        </Label>
        <Hr />
        <Form>
          <MFACode label={formatMessage({id: "platform.mfa.totp.code.label"})} value={code} onChange={handleChange} />
        </Form>
        <ButtonContainer>
          <Button tertiary onClick={onCancel} marginRight={2}>
            <FormattedMessage id="platform.mfa.setup.cancel" />
          </Button>
          <ValidationButton onClick={handleSuccess} primary>
            <FormattedMessage id="platform.mfa.setup.submit" />
          </ValidationButton>
        </ButtonContainer>
      </Loading>
    </Modal>
  )
}

export default injectIntl(MFAPopupSetup)
